import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import { mappingNotice } from "./proposal";
import type { ColumnMap, ImportSkip, MappingProposal, ParsedUpload, ReviewedRow } from "@/types";

/**
 * Step two: say what is in each column.
 *
 * The proposal comes from the writing assistant when it can, and from column
 * names when it cannot. **Which one produced it is always stated.** A silent
 * fall-back to a weaker method is the kind of degradation that looks like
 * everything working until thirty rows land in the wrong column.
 */

export function MappingStep({
  upload,
  onReady,
  onBack,
}: {
  upload: ParsedUpload;
  onReady: (columnMap: ColumnMap, rows: ReviewedRow[], skipped: ImportSkip[]) => void;
  onBack: () => void;
}) {
  const t = useT();
  const { showError } = useToast();

  const [proposal, setProposal] = useState<MappingProposal | undefined>(undefined);
  const [columnMap, setColumnMap] = useState<ColumnMap | undefined>(undefined);
  const [split, setSplit] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    call<{ headers: string[]; rows: string[][] }, { proposal: MappingProposal }>(
      CALLABLES.mapImportColumns,
      { headers: upload.headers, rows: upload.rows.slice(0, 5) }
    )
      .then((response) => {
        if (cancelled) return;
        setProposal(response.proposal);
        setColumnMap(response.proposal.columnMap);
        setSplit(Array.isArray(response.proposal.columnMap.displayName));
        setBusy(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setBusy(false);
        showError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [upload, showError]);

  if (busy || !columnMap || !proposal) {
    return <p className="py-10 text-sm text-ink-muted">{t("common.loading")}</p>;
  }

  const notice = mappingNotice(proposal);

  const nameColumns = Array.isArray(columnMap.displayName)
    ? columnMap.displayName
    : [columnMap.displayName, -1];

  const setName = (position: 0 | 1, value: number) => {
    if (!split) {
      setColumnMap({ ...columnMap, displayName: value });
      return;
    }
    const pair: [number, number] = [nameColumns[0]!, nameColumns[1]!];
    pair[position] = value;
    setColumnMap({ ...columnMap, displayName: pair });
  };

  const setOptional = (field: "role" | "group" | "ageBand", value: number) => {
    const next = { ...columnMap };
    if (value < 0) delete next[field];
    else next[field] = value;
    setColumnMap(next);
  };

  const proceed = async () => {
    setBusy(true);
    try {
      const preview = await call<
        { headers: string[]; rows: string[][]; columnMap: ColumnMap },
        { rows: ReviewedRow[]; skipped: ImportSkip[] }
      >(CALLABLES.previewImportRows, {
        headers: upload.headers,
        rows: upload.rows,
        columnMap,
      });
      // `skipped` is carried through rather than discarded. A row the mapper
      // could not read at all never reaches the grid, and an owner who sent 30
      // rows and sees 29 deserves to be told which one and why.
      onReady(columnMap, preview.rows, preview.skipped);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="section-title">{t("import.mapping.title")}</h2>

      <p className="mt-2 text-sm text-ink-soft">
        {t(notice.sourceKey)}
        <span className={`ml-2 ${notice.tone}`}>{t(notice.confidenceKey)}</span>
      </p>

      {proposal.notes.length > 0 ? (
        <ul className="mt-3 list-disc pl-5 text-sm text-ink-muted">
          {proposal.notes.map((note) => (
            <li key={note}>{t(note)}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-8 flex flex-col gap-4">
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={split}
            onChange={(event) => {
              setSplit(event.target.checked);
              setColumnMap({
                ...columnMap,
                displayName: event.target.checked
                  ? [nameColumns[0] ?? 0, nameColumns[1] === -1 ? 1 : nameColumns[1]!]
                  : (nameColumns[0] ?? 0),
              });
            }}
          />
          {t("import.mapping.splitToggle")}
        </label>

        {split ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <ColumnPicker
              labelKey="import.mapping.first"
              headers={upload.headers}
              value={nameColumns[0]!}
              onChange={(value) => setName(0, value)}
            />
            <ColumnPicker
              labelKey="import.mapping.second"
              headers={upload.headers}
              value={nameColumns[1]!}
              onChange={(value) => setName(1, value)}
            />
          </div>
        ) : (
          <ColumnPicker
            labelKey="import.mapping.field.displayName"
            headers={upload.headers}
            value={nameColumns[0]!}
            onChange={(value) => setName(0, value)}
          />
        )}

        <ColumnPicker
          labelKey="import.mapping.field.role"
          headers={upload.headers}
          value={columnMap.role ?? -1}
          optional
          onChange={(value) => setOptional("role", value)}
        />
        <ColumnPicker
          labelKey="import.mapping.field.group"
          headers={upload.headers}
          value={columnMap.group ?? -1}
          optional
          onChange={(value) => setOptional("group", value)}
        />
        <ColumnPicker
          labelKey="import.mapping.field.ageBand"
          headers={upload.headers}
          value={columnMap.ageBand ?? -1}
          optional
          onChange={(value) => setOptional("ageBand", value)}
        />
      </div>

      <div className="mt-8">
        <p className="field-label">{t("import.mapping.preview")}</p>
        <div className="card overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead>
              <tr className="border-b border-line bg-surface-muted">
                {upload.headers.map((header, position) => (
                  <th
                    key={`${header}-${position}`}
                    scope="col"
                    className="px-3 py-2 text-left font-medium text-ink-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {upload.rows.slice(0, 3).map((row, index) => (
                <tr key={index} className="border-b border-line last:border-0">
                  {upload.headers.map((_, position) => (
                    <td key={position} className="px-3 py-2 text-ink-soft">
                      {row[position] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        <Button variant="ghost" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Button busy={busy} onClick={proceed}>
          {t("import.mapping.continue")}
        </Button>
      </div>
    </div>
  );
}

function ColumnPicker({
  labelKey,
  headers,
  value,
  optional = false,
  onChange,
}: {
  labelKey: string;
  headers: readonly string[];
  value: number;
  optional?: boolean;
  onChange: (value: number) => void;
}) {
  const t = useT();
  return (
    <label>
      <span className="field-label">{t(labelKey)}</span>
      <select
        className="field-input"
        value={String(value)}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {optional ? <option value="-1">{t("import.mapping.none")}</option> : null}
        {headers.map((header, position) => (
          <option key={`${header}-${position}`} value={String(position)}>
            {header}
          </option>
        ))}
      </select>
    </label>
  );
}
